/**
 * ChannelStrip — the five ways a customer can reach a Genie, and whether each
 * one is actually working. Built once, used in two places: the Genie hub Suite
 * page today, and the dashboard REACH band once that branch lands.
 *
 * Every pip is one click from being fixed, and every pip speaks the shared
 * vocabulary in `src/lib/channels/channelVocabulary.ts` — the same words the
 * three channel pages use.
 */
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useChannelStatuses } from "@/hooks/channels/useChannelStatuses";
import { CHANNEL_STATE_LABEL, CHANNEL_STATE_TOKENS } from "@/lib/channels/channelVocabulary";
import { cn } from "@/lib/utils";
import type { ChannelStatus } from "@/types/channels/channel-status";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ChannelStripProps {
  /** "full" shows the detail line; "compact" is icon + state only. */
  variant?: "full" | "compact";
  /** Optional heading above the strip. Omit when the host already has one. */
  title?: string;
  className?: string;
}

const ChannelTile = ({ channel, variant }: { channel: ChannelStatus; variant: "full" | "compact" }) => {
  const tokens = CHANNEL_STATE_TOKENS[channel.state];
  const { fixAction } = channel;

  const body = (
    <>
      <div className="flex items-center gap-2.5 min-w-0">
        <GenieIcon name={channel.genieIcon} size={24} className="h-6 w-6 flex-shrink-0" />
        <span className="text-sm font-semibold text-gray-900 truncate">{channel.label}</span>
        <span className={cn("ml-auto h-2.5 w-2.5 rounded-full flex-shrink-0", tokens.dot)} aria-hidden />
      </div>

      <span className={cn("mt-2 inline-flex self-start rounded-full px-2 py-0.5 text-xs font-medium", tokens.pill)}>
        {CHANNEL_STATE_LABEL[channel.state]}
      </span>

      {variant === "full" && (
        <p className="mt-2 text-xs text-gray-500 leading-snug line-clamp-2">{channel.detail}</p>
      )}

      <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-genie-primary">
        {fixAction.label}
        <ArrowRight className="h-3 w-3" />
      </span>
    </>
  );

  const shell =
    "flex flex-col rounded-2xl border border-gray-200 bg-white p-4 text-left transition-all duration-200 hover:border-genie-primary/40 hover:shadow-md";

  if (fixAction.href) {
    return (
      <a href={fixAction.href} target="_blank" rel="noreferrer" className={shell}>
        {body}
      </a>
    );
  }

  return (
    <Link to={fixAction.to ?? "/genie-hub/my-genies"} className={shell}>
      {body}
    </Link>
  );
};

export const ChannelStrip = ({ variant = "full", title, className }: ChannelStripProps) => {
  const { channels, isLoading, needsAttention } = useChannelStatuses();

  if (isLoading) {
    return (
      <div className={cn("grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5", className)}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="h-[130px] rounded-2xl border border-gray-200 bg-white animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <section className={className} aria-label="Channel status">
      {title && (
        <div className="mb-3 flex items-center gap-3">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          {needsAttention && (
            <span className="rounded-full bg-genie-secondary/10 px-2.5 py-0.5 text-xs font-medium text-genie-secondary">
              Needs attention
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {channels.map((channel) => (
          <ChannelTile key={channel.key} channel={channel} variant={variant} />
        ))}
      </div>
    </section>
  );
};

export default ChannelStrip;
